import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { adminDecideAgentRequest, adminListAgentRequests } from '../api';

// 03_requirements_missing.md story 3 - the website owner's queue of site-visit agent onboarding
// requests raised by rental owners. Approving one creates the `delivery`-role account and reveals
// a one-time temporary password.
export default function AgentRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tempPasswordMsg, setTempPasswordMsg] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRequests(await adminListAgentRequests(filter === 'all' ? undefined : filter));
    } catch {
      setError('Could not load agent requests.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    setBusyId(id);
    setError('');
    try {
      const res = await adminDecideAgentRequest(id, decision);
      if (res.tempPassword) {
        setTempPasswordMsg(`Account created for ${res.createdUser.email}. Temporary password: ${res.tempPassword} (shown once — pass it on).`);
      }
      await load();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to process the decision.');
    } finally {
      setBusyId(null);
    }
  };

  const paged = usePagedArray(requests, { resetKey: filter });

  if (loading) return <Spinner animation="border" role="status" />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Agent requests</h5>
        <Form.Select size="sm" style={{ maxWidth: 140 }} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Agent request status">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </Form.Select>
      </div>
      <p className="text-muted small">Raised by rental owners. Approving one onboards the agent account (a temporary password is shown once).</p>
      {error && <Alert variant="danger" className="py-2">{error}</Alert>}
      {tempPasswordMsg && <Alert variant="success" dismissible onClose={() => setTempPasswordMsg('')}>{tempPasswordMsg}</Alert>}
      <Table responsive hover>
        <thead>
          <tr>
            <th>Candidate</th>
            <th className="d-none d-md-table-cell">Requested by</th>
            <th className="d-none d-md-table-cell">Area / address</th>
            <th>Aadhaar</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {paged.total === 0 && <tr><td colSpan={6}><span className="text-muted">No requests.</span></td></tr>}
          {paged.rows.map((r) => (
            <tr key={r._id}>
              <td>
                {r.name}
                <div className="small text-muted">{r.email}{r.phone ? ` · ${r.phone}` : ''}</div>
                {r.note && <div className="small text-muted">“{r.note}”</div>}
              </td>
              <td className="d-none d-md-table-cell small">{r.requestedByName || '—'}<br /><span className="text-muted">{r.requestedByEmail}</span></td>
              <td className="d-none d-md-table-cell small">
                {r.serviceArea || '—'}
                <div className="text-muted">{[r.address?.line1, r.address?.city, r.address?.pincode].filter(Boolean).join(', ')}</div>
              </td>
              <td className="small">
                {r.kyc?.aadhaarNumber ? `••• ${String(r.kyc.aadhaarNumber).slice(-4)}` : '—'}
                {r.kyc?.aadhaarCardUrl && <div><a href={r.kyc.aadhaarCardUrl} target="_blank" rel="noreferrer">card</a></div>}
                {r.kyc?.selfieUrl && <div><a href={r.kyc.selfieUrl} target="_blank" rel="noreferrer">selfie</a></div>}
                {r.kyc?.drivingLicenceUrl && <div><a href={r.kyc.drivingLicenceUrl} target="_blank" rel="noreferrer">licence</a></div>}
              </td>
              <td>
                <Badge bg={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'danger' : 'warning'} text={r.status === 'pending' ? 'dark' : undefined}>
                  {r.status}
                </Badge>
              </td>
              <td className="text-nowrap">
                {r.status === 'pending' && (
                  <>
                    <Button size="sm" variant="success" className="me-1" disabled={busyId === r._id} onClick={() => decide(r._id, 'approve')}>Approve</Button>
                    <Button size="sm" variant="outline-danger" disabled={busyId === r._id} onClick={() => decide(r._id, 'reject')}>Reject</Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pager {...paged} />
    </div>
  );
}
